import { useState } from "react";
import { Info, Pencil, RefreshCw, Feather } from "lucide-react";
import { OccasionBrowser } from "@/components/site/OccasionBrowser";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { LyricsDisplay, ExplanationNote } from "@/components/site/Audio";
import { Checkbox } from "@/components/ui/checkbox";

const Field = ({ label, hint, children, error }) => (
  <label className="block">
    <span className="label">{label}</span>
    {children}
    {hint && <span className="hint block">{hint}</span>}
    {error && <span className="mt-1 block text-sm text-[var(--rose)]" role="alert">{error}</span>}
  </label>
);

export const StepWho = ({ form, set, catalog, errors }) => {
  const occ = catalog.occasions.find((o) => o.id === form.occasion_id);
  const [browse, setBrowse] = useState(!occ);
  return (
    <div className="space-y-7">
      <Field label="Recipient's name *" error={errors.recipient_name}>
        <input className="field" maxLength={60} value={form.recipient_name} onChange={set("recipient_name")} data-testid="create-wizard-recipient-name" />
      </Field>
      <div>
        <span className="label">Occasion *</span>
        {occ && !browse ? (
          <div className="card-parch flex items-center justify-between px-5 py-4">
            <span className="font-medium" data-testid="create-wizard-selected-occasion">{occ.label}</span>
            <button type="button" className="text-sm text-[var(--rose)] underline" onClick={() => setBrowse(true)} data-testid="create-wizard-change-occasion">Change</button>
          </div>
        ) : (
          <OccasionBrowser compact catalog={catalog} selectedId={form.occasion_id}
            onSelect={(o) => { set("occasion_id")({ target: { value: o.id } }); setBrowse(false); }} />
        )}
        {errors.occasion_id && <p className="mt-1 text-sm text-[var(--rose)]" role="alert">{errors.occasion_id}</p>}
      </div>
      {occ?.allows_custom_label && (
        <Field label="What's the occasion called? (optional)" hint="Up to 60 characters, e.g. “Diwali”, “Eid”, “Our 100th hike”.">
          <input className="field" maxLength={60} value={form.custom_occasion_label} onChange={set("custom_occasion_label")} data-testid="create-wizard-custom-occasion" />
        </Field>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="How should their name be pronounced? (optional)" hint="e.g. “PREE-ya”. Helps our review; exact pronunciation can't be guaranteed.">
          <input className="field" maxLength={120} value={form.pronunciation} onChange={set("pronunciation")} data-testid="create-wizard-pronunciation" />
        </Field>
        <Field label="Preferred spelling in the song (optional)" hint="For names written in another script, e.g. “प्रिया”.">
          <input className="field" maxLength={60} value={form.preferred_spelling} onChange={set("preferred_spelling")} data-testid="create-wizard-preferred-spelling" dir="auto" />
        </Field>
        <Field label="Your relationship (optional)" hint="Use whatever words feel right — “my wife”, “Nani”, “my best friend”.">
          <input className="field" maxLength={60} value={form.relationship} onChange={set("relationship")} data-testid="create-wizard-relationship" />
        </Field>
        <Field label="From (optional)">
          <input className="field" maxLength={60} value={form.sender_name} onChange={set("sender_name")} data-testid="create-wizard-sender-name" />
        </Field>
        <Field label="Event date (optional)" hint="Helps the lyrics. It doesn't guarantee delivery by that date.">
          <input type="date" className="field" value={form.event_date} onChange={set("event_date")} data-testid="create-wizard-event-date" />
        </Field>
      </div>
    </div>
  );
};

export const StepStory = ({ form, set, catalog, errors }) => {
  const occ = catalog.occasions.find((o) => o.id === form.occasion_id);
  const helper = occ?.helper || {};
  return (
    <div className="space-y-7">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl" data-testid="create-wizard-story-heading">{helper.heading}</h2>
        {helper.note && <p className="mt-2 text-sm text-[var(--plum-2)]" data-testid="create-wizard-helper-note">{helper.note}</p>}
        <div className="mt-4 flex flex-wrap gap-2" aria-label="Ideas to write about">
          {(helper.suggestions || []).map((s) => (
            <span key={s} className="rounded-full bg-[var(--rose-soft)] px-3 py-1.5 text-sm text-[var(--rose)]">{s}</span>
          ))}
        </div>
      </div>
      <Field label="Your story or meaningful memories *" error={errors.story}
        hint={`${form.story.length}/3000 · Please don't include sensitive details like health information, addresses or ID numbers.`}>
        <textarea rows={7} maxLength={3000} dir="auto" className="field" value={form.story} onChange={set("story")} data-testid="create-wizard-story-textarea" />
      </Field>
      <p className="flex items-start gap-2 rounded-xl bg-[var(--gold-soft)] p-4 text-sm text-[var(--plum-2)]">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" aria-hidden />
        You can share your story in one language and choose another language for your song. Names are never translated.
      </p>
      <div>
        <span className="label">Emotional tone</span>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Emotional tone">
          {catalog.tones.map((t) => (
            <button type="button" key={t} role="radio" aria-checked={form.tone === t} onClick={() => set("tone")({ target: { value: t } })} data-testid={`create-wizard-tone-${t.toLowerCase()}`}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${form.tone === t ? "border-[var(--plum)] bg-[var(--plum)] text-[var(--ivory)]" : "border-[var(--parchment)] bg-white hover:border-[var(--gold)]"}`}>{t}</button>
          ))}
        </div>
      </div>
      <details className="card-parch p-5">
        <summary className="cursor-pointer font-medium" data-testid="create-wizard-more-details">Add more details (optional)</summary>
        <div className="mt-5 grid gap-5">
          <Field label="Details that must appear"><textarea rows={2} maxLength={500} dir="auto" className="field" value={form.must_include} onChange={set("must_include")} data-testid="create-wizard-must-include" /></Field>
          <Field label="Details to avoid"><textarea rows={2} maxLength={500} dir="auto" className="field" value={form.avoid} onChange={set("avoid")} data-testid="create-wizard-avoid" /></Field>
          <Field label="Words or phrases to include"><input maxLength={300} dir="auto" className="field" value={form.phrases} onChange={set("phrases")} data-testid="create-wizard-phrases" /></Field>
          <Field label="Personal dedication" hint="Shown on the gift page, not sung."><input maxLength={300} dir="auto" className="field" value={form.dedication} onChange={set("dedication")} data-testid="create-wizard-dedication" /></Field>
          <Field label="Language you wrote your story in (optional)"><input maxLength={40} className="field" value={form.story_language} onChange={set("story_language")} data-testid="create-wizard-story-language" /></Field>
        </div>
      </details>
    </div>
  );
};

export const StepSound = ({ form, set, catalog, errors }) => {
  const lang = catalog.languages.find((l) => l.id === form.song_language_id);
  return (
    <div className="space-y-8">
      <div>
        <span className="label">Musical style *</span>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" role="radiogroup" aria-label="Musical style">
          {catalog.styles.map((s) => (
            <button type="button" key={s.id} role="radio" aria-checked={form.style_id === s.id} onClick={() => set("style_id")({ target: { value: s.id } })} data-testid={`create-wizard-style-${s.id}`}
              className={`card-parch p-5 text-left hover:border-[var(--gold)] ${form.style_id === s.id ? "!border-[var(--gold)] ring-2 ring-[var(--gold)]/30" : ""}`}>
              <span className="font-display text-xl">{s.label}</span>
              <span className="mt-1 block text-sm text-[var(--plum-muted)]">{s.description}</span>
            </button>
          ))}
        </div>
        {errors.style_id && <p className="mt-1 text-sm text-[var(--rose)]" role="alert">{errors.style_id}</p>}
      </div>
      <div>
        <span className="label">Song language *</span>
        <LanguageSelector languages={catalog.languages} value={form.song_language_id}
          onChange={(id) => { set("song_language_id")({ target: { value: id } }); set("song_language_variant")({ target: { value: "" } }); }} />
        <p className="hint">The website stays in English. Only languages our team has tested can be ordered.</p>
        {lang?.variants?.length > 0 && (
          <div className="mt-4" role="radiogroup" aria-label="Regional or script preference">
            <span className="label">Regional or script preference *</span>
            <div className="flex flex-wrap gap-2">
              {lang.variants.map((v) => (
                <button type="button" key={v.id} role="radio" aria-checked={form.song_language_variant === v.id} data-testid={`create-wizard-variant-${v.id}`}
                  onClick={() => set("song_language_variant")({ target: { value: v.id } })}
                  className={`rounded-full border px-4 py-2 text-sm ${form.song_language_variant === v.id ? "border-[var(--plum)] bg-[var(--plum)] text-[var(--ivory)]" : "border-[var(--parchment)] bg-white"}`}>{v.label}</button>
              ))}
            </div>
          </div>
        )}
        {errors.song_language_id && <p className="mt-1 text-sm text-[var(--rose)]" role="alert">{errors.song_language_id}</p>}
      </div>
    </div>
  );
};

export const StepLyrics = ({ order, token, busy, onRedraft, onSave, onApprove, maxDrafts }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: "", text: "" });
  const [agree, setAgree] = useState(false);
  if (busy) return (
    <div className="card-parch p-12 text-center" role="status" aria-live="polite" data-testid="lyrics-loading">
      <Feather className="mx-auto h-8 w-8 animate-pulse text-[var(--gold)]" aria-hidden />
      <p className="font-display mt-4 text-2xl">Writing your lyrics…</p>
      <p className="mt-2 text-sm text-[var(--plum-muted)]">This can take up to a minute.</p>
    </div>
  );
  if (!order) return null;
  if (order.manual_drafting && !order.lyrics) return (
    <div className="card-parch p-10" data-testid="lyrics-manual">
      <p className="font-display text-2xl">Our team will write your lyrics</p>
      <p className="mt-2 text-[var(--plum-2)]">Automatic drafting isn't available right now. Keep your order link — you'll approve the lyrics there before paying.</p>
    </div>
  );
  const l = order.lyrics;
  if (!l || order.lyrics_stale) return (
    <div className="card-parch p-10" data-testid="lyrics-stale">
      <p className="font-display text-2xl">{order.lyrics_stale ? "The song language changed" : "No lyrics yet"}</p>
      <p className="mt-2 text-[var(--plum-2)]">{order.lyrics_stale ? "Your earlier approval no longer applies. We'll prepare a new draft in the selected language for you to approve." : "Let's draft your lyrics."}</p>
      <button className="btn-gold mt-6" onClick={onRedraft} data-testid="lyrics-draft-new">Draft lyrics</button>
    </div>
  );
  const lang = order.song_language;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[var(--plum-muted)]">Version {l.version} · Sung in <strong>{lang.english_name}</strong> <span className="bidi-isolate" lang={lang.code}>({lang.native_name})</span>{order.song_language_variant_label && ` · ${order.song_language_variant_label}`}</p>
        <div className="flex gap-2">
          {!editing && <button className="btn-ghost !px-4 !py-2 text-sm" onClick={() => { setDraft({ title: l.title, text: l.text }); setEditing(true); }} data-testid="lyrics-edit-button"><Pencil className="h-4 w-4" />Edit</button>}
          {!editing && order.lyric_drafts_used < maxDrafts && <button className="btn-ghost !px-4 !py-2 text-sm" onClick={onRedraft} data-testid="lyrics-redraft-button"><RefreshCw className="h-4 w-4" />New draft ({maxDrafts - order.lyric_drafts_used} left)</button>}
        </div>
      </div>
      {editing ? (
        <div className="card-white space-y-4 p-6">
          <label className="block"><span className="label">Song title</span>
            <input className="field" dir={lang.rtl ? "rtl" : "auto"} lang={lang.code} maxLength={80} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="lyrics-edit-title" /></label>
          <label className="block"><span className="label">Lyrics</span>
            <textarea rows={16} className="field lyrics-text" dir={lang.rtl ? "rtl" : "auto"} lang={lang.code} maxLength={5000} value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} data-testid="lyrics-edit-textarea" /></label>
          <p className="hint">Keep section labels like [Verse 1] and [Chorus] on their own lines. Saving creates a new version that you'll need to approve.</p>
          <div className="flex gap-3">
            <button className="btn-plum" onClick={async () => { await onSave(draft); setEditing(false); setAgree(false); }} data-testid="lyrics-save-button">Save lyrics</button>
            <button className="btn-ghost" onClick={() => setEditing(false)} data-testid="lyrics-cancel-edit">Cancel</button>
          </div>
        </div>
      ) : (
        <article className="card-white p-8 sm:p-10">
          <h2 className="font-display text-3xl" lang={lang.code} dir={lang.rtl ? "rtl" : "ltr"} data-testid="lyrics-title">{l.title}</h2>
          <div className="mt-6"><LyricsDisplay text={l.text} language={lang} /></div>
        </article>
      )}
      {!editing && <ExplanationNote token={token} version={l.version} language={lang} initial={l.explanation} />}
      {!editing && (order.lyrics_approved ? (
        <p className="rounded-xl bg-[var(--gold-soft)] p-4 text-sm font-medium" data-testid="lyrics-approved-state">You approved version {l.version}. Continue to review your order.</p>
      ) : (
        <div className="card-parch space-y-4 p-6">
          <label className="flex items-start gap-3">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="lyrics-approve-checkbox" className="mt-0.5" />
            <span className="text-sm">I've reviewed these lyrics and approve them as the words that will be sung. I understand AI vocals may not pronounce every word exactly.</span>
          </label>
          <button className="btn-gold" disabled={!agree} onClick={() => onApprove(l.version)} data-testid="lyrics-approve-button">Approve lyrics</button>
        </div>
      ))}
    </div>
  );
};
